import { Autocomplete, Box, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import type { ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { formatUIDateTime,  endOfDayIso, formatBoxPieces, startOfDayIso, toDateInput } from '@tiles-erp/shared';
import { usePagination } from '@tiles-erp/hooks';
import type { ProductItem, StockMovementItem } from '@tiles-erp/shared-types';
import { DateField,  PageContainer } from '@tiles-erp/ui';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { useSessionBranchId } from '../../lib/session-branch';
import { useBranches } from '../products/branch-prices-api';
import { useProducts } from '../products/api';
import { useGodowns, useProductActivity, useProductStockCheck } from './api';

const dateDaysAgo = (days: number): string => {
  const value = new Date();
  value.setDate(value.getDate() - days);
  return toDateInput(value);
};

const MOVEMENT_LABELS: Record<string, string> = {
  OPENING: 'Opening stock', PURCHASE: 'Purchase', PURCHASE_RETURN: 'Purchase return',
  SALE: 'Sales invoice', SALE_RETURN: 'Sales return', ADJUSTMENT: 'Stock adjustment',
  TRANSFER_OUT: 'Transfer out', TRANSFER_IN: 'Transfer in', SAMPLE_OUT: 'Sample out',
  SAMPLE_IN: 'Sample return',
};

/** One product's complete stock trail, so quantity changes can be traced to documents and staff. */
export function ProductActivityReportPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 100 });
  const [productSearch, setProductSearch] = useState('');
  const [product, setProduct] = useState<ProductItem | null>(null);
  const [branchId, setBranchId] = useSessionBranchId();
  const [godownId, setGodownId] = useState('');
  const [fromDate, setFromDate] = useState(() => dateDaysAgo(7));
  const [toDate, setToDate] = useState(() => toDateInput(new Date()));
  const branches = useBranches();
  const godowns = useGodowns(branchId || undefined);
  const products = useProducts(
    { page: 1, pageSize: 100, search: productSearch, sortOrder: 'asc' },
    {},
  );
  const activity = useProductActivity(
    pagination.query,
    {
      productId: product?.id,
      branchId: branchId || undefined,
      godownId: godownId || undefined,
      fromDate: fromDate ? startOfDayIso(fromDate) : undefined,
      toDate: toDate ? endOfDayIso(toDate) : undefined,
    },
    Boolean(product && branchId),
  );
  const current = useProductStockCheck(product?.id ?? '');
  const branchStock = current.data?.find((row) => row.branchId === branchId);

  const columns = useMemo<ColDef<StockMovementItem>[]>(() => [
    {
      field: 'movementDate', headerName: 'Date & time', minWidth: 170,
      valueFormatter: (p) => p.value ? formatUIDateTime(new Date(p.value as string)) : '',
    },
    { field: 'godownName', headerName: 'Godown', minWidth: 150 },
    {
      field: 'type', headerName: 'Activity', minWidth: 150,
      valueFormatter: (p) => MOVEMENT_LABELS[String(p.value)] ?? String(p.value ?? ''),
    },
    {
      headerName: 'Quantity', minWidth: 145,
      valueGetter: (p) => p.data
        ? `${p.data.direction === 'IN' ? '+' : '−'} ${formatBoxPieces(p.data.qtyBoxes, p.data.piecesPerBox, p.data.baseUom === 'PIECE')}`
        : '',
      cellStyle: (p) => ({
        fontWeight: 700,
        color: String(p.value).startsWith('+') ? '#1e874b' : '#c62828',
      }),
    },
    { field: 'refNumber', headerName: 'Document / reference', minWidth: 170 },
    { field: 'reason', headerName: 'Reason', minWidth: 170 },
    { field: 'remarks', headerName: 'Remarks', minWidth: 180 },
    { field: 'createdByName', headerName: 'Updated by', minWidth: 150 },
  ], []);

  return (
    <PageContainer
      title="Product activity report"
      subtitle="Trace every stock addition, sale, return, transfer and physical-count adjustment for one product."
    >
      <Stack spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
            <Autocomplete
              options={products.data?.items ?? []}
              loading={products.isFetching}
              value={product}
              inputValue={productSearch}
              onChange={(_, value) => { setProduct(value); pagination.setPage(1); }}
              onInputChange={(_, value) => setProductSearch(value)}
              filterOptions={(options) => options}
              getOptionLabel={(option) => `${option.sku} · ${option.name}${option.sizeMm ? ` · ${option.sizeMm}` : ''}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={productSearch ? 'No matching product' : 'Type a SKU or product name'}
              renderInput={(params) => <TextField {...params} label="Product" placeholder="Type SKU or product name" size="small" />}
              sx={{ width: { xs: '100%', md: 390 } }}
            />
            <SearchableSelect label="Branch" value={branchId}
              options={(branches.data ?? []).map((branch) => ({ value: branch.id, label: branch.name }))}
              onChange={(value) => { setBranchId(value); setGodownId(''); pagination.setPage(1); }} width={220} />
            <SearchableSelect label="Godown" value={godownId} allLabel="All godowns"
              options={(godowns.data ?? []).map((godown) => ({ value: godown.id, label: godown.name }))}
              onChange={(value) => { setGodownId(value); pagination.setPage(1); }} disabled={!branchId} width={190} />
            <DateField label="From" type="date" size="small" value={fromDate}
              onChange={(event) => { setFromDate(event.target.value); pagination.setPage(1); }}
              slotProps={{ inputLabel: { shrink: true } }} />
            <DateField label="To" type="date" size="small" value={toDate}
              onChange={(event) => { setToDate(event.target.value); pagination.setPage(1); }}
              slotProps={{ inputLabel: { shrink: true } }} />
          </Stack>
        </Paper>

        {product && branchId && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Typography variant="subtitle1" fontWeight={800}>{product.sku} · {product.name}</Typography>
            {branchStock && (
              <Chip color="primary" label={`Current branch stock: ${formatBoxPieces(branchStock.currentQtyBoxes, branchStock.piecesPerBox, branchStock.baseUom === 'PIECE')}`} />
            )}
          </Stack>
        )}

        {!product ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>Select a product to view its stock activity.</Box>
        ) : (
          <DataTable
            rows={activity.data?.items ?? []}
            columns={columns}
            meta={activity.data?.meta}
            pagination={pagination}
            loading={activity.isFetching}
            searchPlaceholder="Search reference…"
            exportTitle={`Product activity - ${product.sku} ${product.name}`}
            exportFilename={`product-activity-${product.sku}`}
            height={620}
          />
        )}
      </Stack>
    </PageContainer>
  );
}
